import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SadeedStory from "@/components/SadeedStory";
import { projectText, sadeedCaseStudy } from "@/lib/project";
import { imagekitWidth } from "@/lib/imagekit";
import { API_BASE_URL } from "@/lib/apiBase";
import { mediaUrl } from "@/lib/mediaUrl";

const LABELS = {
  ar: { back: "العودة إلى الأعمال", loading: "جارٍ تحميل المشروع...", unavailable: "تعذر تحميل المشروع الآن.", missing: "هذا المشروع غير متاح.", problem: "التحدي", solution: "ما أنجزته", outcome: "الوضع الحالي", visit: "زيارة موقع سديد", video: "شاهد عرض المنتج" },
  en: { back: "Back to work", loading: "Loading project...", unavailable: "The project could not be loaded right now.", missing: "This project is unavailable.", problem: "The challenge", solution: "What I built", outcome: "Current status", visit: "Visit Sadeed", video: "Watch the product video" },
  fr: { back: "Retour aux projets", loading: "Chargement du projet...", unavailable: "Impossible de charger le projet actuellement.", missing: "Ce projet n'est pas disponible.", problem: "Le défi", solution: "Ma contribution", outcome: "État actuel", visit: "Voir Sadeed", video: "Voir la vidéo du produit" },
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = LABELS[lang];
  const [project, setProject] = useState(slug === "sadeed" ? sadeedCaseStudy : null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setProject(slug === "sadeed" ? sadeedCaseStudy : null);
    setError("");
    if (slug === "sadeed") return () => { cancelled = true; };
    fetch(`${API_BASE_URL}/api/projects/${encodeURIComponent(slug)}`)
      .then((response) => {
        if (!response.ok) throw new Error(response.status === 404 ? "missing" : "unavailable");
        return response.json();
      })
      .then((data) => { if (!cancelled) setProject(data); })
      .catch((reason) => { if (!cancelled) setError(reason.message === "missing" ? "missing" : "unavailable"); });
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (project) document.title = `${projectText(project, "title", lang)} — YacoubCreates`;
    return () => { document.title = "YacoubCreates"; };
  }, [project, lang]);

  const title = projectText(project, "title", lang);
  const sections = ["problem", "solution", "outcome"];

  return <div className="min-h-screen bg-softwhite text-charcoal">
    <header className="border-b border-line bg-white/90"><div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-5">
          <Link to="/"><img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="h-7 w-auto" /></Link>
      <LanguageSwitcher />
    </div></header>
    <main className="mx-auto max-w-6xl px-5 py-10 md:py-16">
      <Link to="/#work" className="inline-flex items-center gap-2 text-sm font-semibold text-sage-deep"><ArrowLeft className="size-4 rtl:rotate-180" />{t.back}</Link>
      {slug === "sadeed" ? <SadeedStory lang={lang} /> : <>
      {error && <p className="mt-16 text-center text-graphite/70">{t[error]}</p>}
      {!project && !error && <p className="mt-16 text-center text-graphite/70">{t.loading}</p>}
      {project && <article>
        <div className="mt-12 max-w-3xl">
          {projectText(project, "type", lang) && <span className="rounded-full bg-mist px-4 py-2 text-sm font-semibold text-sage-deep">{projectText(project, "type", lang)}</span>}
          <h1 className="mt-7 font-display text-4xl font-bold leading-tight md:text-6xl">{title}</h1>
          {projectText(project, "statement", lang) && <p className="mt-6 text-lg leading-relaxed text-graphite/75">{projectText(project, "statement", lang)}</p>}
          {project.external_url && <a href={project.external_url} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 text-sm font-semibold text-white">{t.visit}<ExternalLink className="size-4" /></a>}
        </div>
        {project.cover_url && <img src={imagekitWidth(project.cover_url, 1800)} alt={title} className="mt-12 max-h-[660px] w-full rounded-3xl object-cover" />}
        {project.video_url && <section className="mt-12"><h2 className="mb-5 font-display text-2xl font-bold">{t.video}</h2><video controls playsInline preload="metadata" poster={project.video_poster || undefined} className="w-full rounded-3xl bg-charcoal" src={project.video_url}>{t.video}</video></section>}
        <div className="mt-14 grid gap-10 md:grid-cols-3">
          {sections.map((key) => { const value = projectText(project, key, lang); return value && <section key={key} className="border-t border-line pt-5"><h2 className="font-display text-2xl font-bold">{t[key]}</h2><p className="mt-4 whitespace-pre-line leading-8 text-graphite/75">{value}</p></section>; })}
        </div>
        {Array.isArray(project.gallery) && project.gallery.length > 0 && <div className="mt-14 grid gap-5 md:grid-cols-2">{project.gallery.map((url, index) => <img key={`${url}-${index}`} src={imagekitWidth(url, 1200)} alt={`${title} ${index + 1}`} loading="lazy" className="w-full rounded-3xl object-cover" />)}</div>}
      </article>}
      </>}
    </main>
  </div>;
};

export default ProjectDetail;
